// ADMIN (only for the server's admins): upload map pk3s (checked by the
// server), rebuild a map's bundle, give it an official room, set difficulty
// and tags, remove it; and every room on the server.

import { useEffect, useState } from 'preact/hooks';
import { MAP_TAGS, type AdminStatus } from '../../shared/net/protocol';
import { Button } from '../components/Button';
import { Panel } from '../components/Panel';
import { Toggle } from '../components/Toggle';
import { useApp } from '../context';
import { mb } from '../maps/useCatalog';
import { confirmDialog, net, toast } from '../state';

export function AdminScreen() {
  const app = useApp();
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [replace, setReplace] = useState(false);

  const refresh = () =>
    app.adminStatus().then(
      (s) => {
        setStatus(s);
        setError(null);
      },
      (err: Error) => setError(err.message),
    );
  useEffect(() => void refresh(), [net.value.maps]);

  /** Runs an admin request with a busy marker and a toast. */
  const run = async (what: string, request: () => Promise<unknown>, done: string) => {
    setBusy(what);
    try {
      await request();
      toast(done, 'good');
    } catch (err) {
      toast(`${what}: ${(err as Error).message}`, 'bad', 7000);
    } finally {
      setBusy(null);
      void refresh();
    }
  };

  if (!net.value.client?.admin) return <div class="screen admin-screen empty-state">Admins only.</div>;
  return (
    <div class="screen admin-screen">
      <Panel title="Upload a map" class="admin-upload">
        <p class="muted small">
          A .pk3 with one or more maps (maps/*.bsp, ET's IBSP 47), at most {status ? mb(status.maxUploadBytes) : '…'}. Executables and game code are refused. Textures the map needs from ET's
          data come from the server's base pk3s. It goes to <span class="mono">{status?.uploadDir ?? '…'}</span> and is playable right away.
        </p>
        <div class="row-buttons">
          <input type="file" accept=".pk3" aria-label="Map pk3" onChange={(e) => setFile((e.currentTarget as HTMLInputElement).files?.[0] ?? null)} />
          <Toggle label="Replace maps already on the server" checked={replace} onChange={setReplace} />
          <Button
            variant="primary"
            icon="upload"
            disabled={!file || !!busy}
            onClick={() => file && run('Upload', () => app.adminUpload(file, replace).then((maps) => toast(`Added ${maps.join(', ')}`, 'good')), `${file.name} uploaded`)}
          >
            {busy === 'Upload' ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </Panel>

      {error && <p class="warn-text">{error}</p>}

      <Panel title={`Maps · ${status?.maps.length ?? 0}`}>
        <table class="records-table admin-maps">
          <thead>
            <tr>
              <th>Map</th>
              <th>Bundle</th>
              <th>Official room</th>
              <th>Difficulty</th>
              <th>Tags</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {status?.maps.map((m) => (
              <tr key={m.name}>
                <td>
                  <div class="cell-title mono">{m.name}</div>
                  <div class="muted small">
                    {m.source ?? '—'} · {m.players} playing
                  </div>
                </td>
                <td class="mono small">{mb(m.bundleSize)}</td>
                <td>
                  <Toggle label={m.official ? 'Yes' : 'No'} checked={m.official} onChange={(on) => run('Official room', () => app.adminOfficial(m.name, on), on ? `${m.name} has an official room` : `${m.name} has no official room`)} />
                </td>
                <td>
                  <select class="input" value={m.difficulty ?? ''} aria-label={`Difficulty of ${m.name}`} onChange={(e) => run('Difficulty', () => app.adminMeta(m.name, { difficulty: (e.currentTarget as HTMLSelectElement).value ? Number((e.currentTarget as HTMLSelectElement).value) : null }), 'Saved')}>
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <div class="chips">
                    {MAP_TAGS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        class={`chip small${m.tags.includes(t) ? ' active' : ''}`}
                        aria-pressed={m.tags.includes(t)}
                        onClick={() => run('Tags', () => app.adminMeta(m.name, { tags: m.tags.includes(t) ? m.tags.filter((x) => x !== t) : [...m.tags, t] }), 'Saved')}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </td>
                <td>
                  <div class="row-buttons">
                    <Button variant="ghost" size="sm" icon="restart" disabled={!!busy} onClick={() => run('Rebuild', () => app.adminRebuild(m.name), `${m.name} rebuilt`)}>
                      Rebuild
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="trash"
                      disabled={!!busy}
                      onClick={async () => {
                        if (!(await confirmDialog({ title: `Remove ${m.name}?`, body: 'Its rooms close and an uploaded pk3 is deleted.', confirm: 'Remove', danger: true }))) return;
                        await run('Remove', () => app.adminRemove(m.name), `${m.name} removed`);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title={`Rooms · ${status?.rooms.length ?? 0}`}>
        <ul class="plain-list">
          {status?.rooms.map((r) => (
            <li key={r.id} class="admin-room">
              <span>
                {r.name} {r.official && <span class="chip static chip-info">Official</span>} {r.private && <span class="chip static">Private</span>}
              </span>
              <span class="muted mono">
                {r.map} · {r.players}/{r.maxPlayers}
                {r.owner ? ` · ${r.owner}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
